import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { Link, useLocation } from 'wouter'
import { ArrowLeft, User, Building, DollarSign, Calendar, CheckCircle, XCircle, Clock, Eye, MessageSquare, FileText } from 'lucide-react'

import toast from 'react-hot-toast'
import { buildApiUrl } from '@/config/api'

interface RfpResponse {
  id: string
  message: string
  proposal: string
  budget: number
  timeline: string
  status: 'PENDING' | 'REVIEWED' | 'ACCEPTED' | 'REJECTED' | 'WITHDRAWN'
  attachments: string[]
  createdAt: string
  updatedAt: string
  rfp: {
    id: string
    title: string
    status: string
    category: string
    budgetMin: number
    budgetMax: number
    budgetCurrency: string
    deadline: string
    user: {
      id: string
      name: string
      company: string
    }
  }
}

export function MyResponses() {

  const [, setLocation] = useLocation()
  const [responses, setResponses] = useState<RfpResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchMyResponses = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          toast.error('Authentication required')
          setLocation('/login')
          return
        }

        const response = await fetch(buildApiUrl('/api/responses/my-responses'), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          setResponses(data.data)
        } else {
          setError('Failed to load your responses')
        }
      } catch (error) {
        console.error('Error fetching responses:', error)
        setError('Failed to load your responses')
      } finally {
        setIsLoading(false)
      }
    }

    fetchMyResponses()
  }, [setLocation])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-4 w-4 text-yellow-500" />
      case 'REVIEWED':
        return <Eye className="h-4 w-4 text-blue-500" />
      case 'ACCEPTED':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'REJECTED':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'WITHDRAWN':
        return <FileText className="h-4 w-4 text-gray-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'REVIEWED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'REJECTED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'WITHDRAWN':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Responses</h1>
            <p className="text-muted-foreground">
              Track your RFP responses and their status
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading your responses...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Error</h1>
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">{error}</p>
            <Link href="/">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Responses</h1>
            <p className="text-muted-foreground">
              {responses.length} response{responses.length !== 1 ? 's' : ''} submitted
            </p>
          </div>
        </div>
      </div>

      {/* Responses List */}
      {responses.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No responses yet</h3>
            <p className="text-muted-foreground">
              Your responses to RFPs will appear here once you submit proposals.
            </p>
            <Link href="/request-for-proposal-marketplace">
              <Button className="mt-4">
                Browse RFPs
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {responses.map((response) => (
            <Card key={response.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">
                      <Link href={`/rfp/${response.rfp.id}`} className="hover:underline">
                        <span className="text-primary hover:text-primary/80 transition-colors">
                          {response.rfp.title}
                        </span>
                      </Link>
                    </CardTitle>
                    <CardDescription className="mt-2">
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <User className="h-4 w-4" />
                          <span>{response.rfp.user.name}</span>
                        </div>
                        {response.rfp.user.company && (
                          <div className="flex items-center space-x-1">
                            <Building className="h-4 w-4" />
                            <span>{response.rfp.user.company}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-1">
                          <DollarSign className="h-4 w-4" />
                          <span>
                            {formatCurrency(response.rfp.budgetMin, response.rfp.budgetCurrency)} - {formatCurrency(response.rfp.budgetMax, response.rfp.budgetCurrency)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4" />
                          <span>Deadline: {formatDate(response.rfp.deadline)}</span>
                        </div>
                      </div>
                    </CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(response.status)}`}>
                      {getStatusIcon(response.status)}
                      <span className="ml-1">{response.status}</span>
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Your Proposal</h4>
                                            <p className="text-sm text-muted-foreground line-clamp-3 overflow-hidden text-ellipsis break-words">
                      {response.proposal}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Your Budget:</span>
                      <p className="text-muted-foreground">{formatCurrency(response.budget, response.rfp.budgetCurrency)}</p>
                    </div>
                    <div>
                      <span className="font-medium">Your Timeline:</span>
                      <p className="text-muted-foreground">{response.timeline}</p>
                    </div>
                    <div>
                      <span className="font-medium">Submitted:</span>
                      <p className="text-muted-foreground">{formatDate(response.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 pt-2">
                    <Link href={`/rfp/${response.rfp.id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="mr-2 h-4 w-4" />
                        View RFP
                      </Button>
                    </Link>
                    {response.attachments.length > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {response.attachments.length} attachment{response.attachments.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
} 